import { ConfiguredMiddleware } from 'wretch';

// This is a mock of the Cache API for development on non-SSL-enabled environments.
/* eslint-disable */
const inMemoryGlobalCaches = () => {
  return {
    async open(_name: string) {
      const cache = new Map<string, Response>();
      return {
        async match(key: string) {
          return cache.get(key);
        },
        async put(key: string, value: Response) {
          return cache.set(key, value);
        },
        async delete(key: string) {
          cache.delete(key);
        }
      } as unknown as Cache;
    }
  } as unknown as CacheStorage;
};
/* eslint-enable */

const globalCache = global.caches || inMemoryGlobalCaches();

// Fallback expiration for cached responses.
const defaultExpiresAfterMinutes = 10;

const isExpired = (resp: Response, expireIfOlderThan: string | undefined): boolean => {
  const timestamp = Date.parse(resp.headers.get('Date') as string);
  const expirationDate = expireIfOlderThan
    ? Date.parse(expireIfOlderThan)
    : Date.now() - 60 * defaultExpiresAfterMinutes * 1000;
  return expirationDate > timestamp;
};

const removeFromCacheIfExpired = async (
  cache: Cache,
  key: string,
  expireIfOlderThan: string | undefined
): Promise<boolean> => {
  const resp = await cache.match(key);
  if (!resp) return false;

  const shouldExpire = isExpired(resp, expireIfOlderThan);

  if (shouldExpire) {
    void cache.delete(key);
  }
  return shouldExpire;
};

type CacheMiddlewareFactoryOptions = {
  cache: string;
};

type CacheMiddlewareOptions = {
  cacheId?: string;
  expireIfOlderThan?: string; // Date-parseable string
  /**
   * When set together with `cacheId`, the entry is stored under
   * `<origin>/<namespace>/<keyPrefix>/<cacheId>`. That key does not depend on the page that is
   * showing, unlike the legacy `?<cacheId>` key, which the Cache API resolves against the current
   * page address. It also lets a caller scope every entry to one "generation" of data.
   */
  keyPrefix?: string;
  /**
   * Send the request without looking in the cache first, but still store the response and share it
   * with identical requests made while it is in flight. For callers that know the entry cannot
   * exist yet, such as the first request in a brand-new key prefix. The lookup only resolves once
   * the main thread is free, so skipping it lets the request go out even while the page is busy.
   * An identical request already in flight, or an answer that arrived moments ago and is still
   * being written, is reused instead of sending another.
   */
  skipLookup?: boolean;
};

export type CacheMiddleware = ((reqOptions?: CacheMiddlewareOptions) => ConfiguredMiddleware) & {
  /**
   * Drops the answers held in memory while their cache writes land. Call it whenever the
   * namespace is wiped, so nothing fetched before the wipe can be served after it.
   */
  forgetPending: () => void;
};

// Safety limit for holding an answer whose cache write never settles.
const maxHeldMilliseconds = 60_000;

const ignoreFailure = (): undefined => undefined;

const resolveCacheKey = (namespace: string, reqOptions: CacheMiddlewareOptions, url: string): string => {
  if (!reqOptions.cacheId) return url;
  // If `reqOptions.cacheId` is provided, it's probably a non-GET request, so we prepend a `?` to the key
  // ensure a working url cache key for the Cache API.
  if (!reqOptions.keyPrefix) return `?${reqOptions.cacheId}`;
  // `location` does not exist outside a browser; the key only has to be a valid, stable URL.
  const origin = typeof location === 'undefined' ? 'http://localhost' : location.origin;
  return `${origin}/${namespace}/${reqOptions.keyPrefix}/${reqOptions.cacheId}`;
};

/**
 * The Cache API resolves a relative key such as `?<cacheId>` against the address of the page, so an
 * entry written on one page is not found on another. Answers held in memory follow the same rule.
 */
const holdKeyFor = (key: string): string => (typeof location === 'undefined' ? key : new URL(key, location.href).href);

// This is Wretch middleware that caches GET requests in the browser's Cache API.
// It also dedupes requests, so if multiple requests are made for the same url,
// only one request will be made to the server.
//
// Usage:
// Instantiate the middleware factory with a cache name:
// const cache = cacheMiddleware({ cache: 'some-cache-namespace' });
//
// `cache` is a function that can be used in Wretch's `middlewares` method:
//
// const pivotRows = transport.middlewares([ cache() ])

/* eslint-disable */
export const cacheMiddlewareFactory = (options: CacheMiddlewareFactoryOptions): CacheMiddleware => {
  // Each request in flight owns its list of waiters. A list stays in the map only while it is the
  // current one for its key: `forgetPending` drops them, so an answer to a request sent before a
  // wipe still reaches its own waiters but is neither shared with later requests nor held.
  const inflight = new Map<string, [(value: Response) => void, (reason: unknown) => void][]>();
  // Responses that have arrived but whose cache write has not landed yet. The write only completes
  // once the main thread is free, which can be seconds while a page is rendering; without this, an
  // identical request in that window is neither in flight nor cached and goes to the network again.
  const awaitingWrite = new Map<string, Response>();

  const servedWhileWriting = (key: string, expireIfOlderThan: string | undefined): Response | undefined => {
    const held = awaitingWrite.get(holdKeyFor(key));
    return held && !isExpired(held, expireIfOlderThan) ? held.clone() : undefined;
  };

  const release = (holdKey: string, held: Response): void => {
    if (awaitingWrite.get(holdKey) === held) awaitingWrite.delete(holdKey);
  };

  const holdUntilWritten = (key: string, held: Response, written: Promise<unknown>): void => {
    const holdKey = holdKeyFor(key);
    awaitingWrite.set(holdKey, held);
    const safetyRelease = setTimeout(() => release(holdKey, held), maxHeldMilliseconds);
    written.catch(ignoreFailure).finally(() => {
      clearTimeout(safetyRelease);
      release(holdKey, held);
    });
  };

  const joinInFlight = (key: string): Promise<Response> =>
    new Promise((resolve, reject) => {
      inflight.get(key)?.push([resolve, reject]);
    });

  /**
   * An identical request already in flight, or an answer that has arrived and is still being
   * written. Either is newer than anything the cache has stored for the key.
   */
  const alreadyAnswered = (
    key: string,
    expireIfOlderThan: string | undefined
  ): Promise<Response> | Response | undefined =>
    inflight.has(key) ? joinInFlight(key) : servedWhileWriting(key, expireIfOlderThan);

  const shareWhenAnswered = (key: string, sent: Promise<Response>, openCache: Promise<Cache>) => {
    const waiters: [(value: Response) => void, (reason: unknown) => void][] = [];
    inflight.set(key, waiters);
    const stopSharing = (): boolean => {
      const isCurrent = inflight.get(key) === waiters;
      if (isCurrent) inflight.delete(key);
      return isCurrent;
    };

    const onAnswer = (response: Response): Response => {
      const isCurrent = stopSharing();
      // Check if the response status is either 200 (OK) or 204 (No Content)
      if (isCurrent && (response.status === 200 || response.status === 204)) {
        // Cache the cloned response for successful requests
        const cachedCopy = response.clone();
        holdUntilWritten(
          key,
          response.clone(),
          openCache.then((cache) => cache.put(key, cachedCopy))
        );
      }
      waiters.forEach(([resolve]) => resolve(response.clone()));
      return response;
    };

    const onFailure = (error: unknown): never => {
      stopSharing();
      waiters.forEach(([, reject]) => reject(error));
      throw error;
    };

    return sent.then(onAnswer, onFailure);
  };

  const middleware =
    (reqOptions: CacheMiddlewareOptions = {}): ConfiguredMiddleware =>
    (next) =>
    async (url, opts) => {
      const key = resolveCacheKey(options.cache, reqOptions, url);
      const answeredBefore = alreadyAnswered(key, reqOptions.expireIfOlderThan);
      if (answeredBefore) return answeredBefore;

      if (reqOptions.skipLookup) {
        const sent = next(url, opts);
        const openCache = globalCache.open(options.cache);
        // Opened before anything awaits it; a failure here only means the answer is not cached.
        openCache.catch(ignoreFailure);
        return shareWhenAnswered(key, sent, openCache);
      }

      const cache = await globalCache.open(options.cache);
      const cachedResponse = await cache.match(key);

      // Something may have been sent or answered while the cache was being read.
      const answeredMeanwhile = alreadyAnswered(key, reqOptions.expireIfOlderThan);
      if (answeredMeanwhile) return answeredMeanwhile;

      if (cachedResponse) {
        const expired = await removeFromCacheIfExpired(cache, key, reqOptions.expireIfOlderThan);
        if (!expired) {
          return Promise.resolve(cachedResponse.clone());
        }
      }

      return shareWhenAnswered(key, next(url, opts), Promise.resolve(cache));
    };

  const forgetPending = (): void => {
    awaitingWrite.clear();
    inflight.clear();
  };

  return Object.assign(middleware, { forgetPending });
};
/* eslint-enable */
